import { Localized } from "@fluent/react"
import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material"
import { ReactNode, useState } from "react"
import { CancelButton } from "../../../app/components/CancelButton"
import { DeleteButton } from "../../../app/components/DeleteButton"
import { AppId } from "../../../app/appTypes"
import { useAppDispatch } from "../../../app/hooks"
import { deleteURSRequirementWithReason, URSRequirement } from "../UrsSlice"

const DELETE_REASON_MAX_LENGTH = 1000

export interface DeleteRequirementDialogProps {
    entity: URSRequirement
    onSuccess: () => void
    onCancel: () => void
    typeId: AppId
    group: string
    // What happens after the deletion lands, when that is not simply "the dialog
    // closes". The edit dialog moves the user to the next requirement, which is
    // worth saying before they commit rather than after.
    hint?: ReactNode
}
export const DeleteRequirementDialog = ({ entity, onSuccess, onCancel, typeId, group, hint }: DeleteRequirementDialogProps) => {
    const dispatch = useAppDispatch()
    const [saving, setSaving] = useState(false)
    const [reason, setReason] = useState("")
    const requirementNumber = entity.numeration ?? entity.code ?? entity.name

    const handleConfirmDelClick = async () => {
        setSaving(true)
        try {
            await dispatch(deleteURSRequirementWithReason({ entity, reason: reason.trim() })).unwrap()
            onSuccess()
        } catch (e) {
            console.log(e)
        } finally {
            setSaving(false)
        }
    }
    const handleCancelDelClick = () => {
        onCancel()
    }

    return <Dialog open={true} fullWidth maxWidth="xs">
        <DialogTitle
            sx={(t) => ({
                bgcolor: t.palette.grey[100],
                borderBottom: `1px solid ${t.palette.divider}`,
                py: 1,
                fontWeight: 600,
                fontSize: t.typography.h6.fontSize,
            })}
        >
            <Localized id="delete">
                <span>Usuwanie wymagania</span>
            </Localized>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
            <DialogContentText sx={{ color: 'text.primary' }}>
                <Localized id="confirm-delete-requirement" vars={{ requirementNumber: requirementNumber ?? '' }}>
                    <span>
                        Czy na pewno chcesz usunąć wymaganie <strong>{`"${requirementNumber}"`}</strong>?
                    </span>
                </Localized>
            </DialogContentText>
            <TextField
                autoFocus
                fullWidth
                size="small"
                margin="normal"
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, DELETE_REASON_MAX_LENGTH))}
                inputProps={{ maxLength: DELETE_REASON_MAX_LENGTH }}
                label={<Localized id="delete-requirement-reason">
                    <span>Powód usunięcia</span>
                </Localized>}
                helperText={`${reason.length}/${DELETE_REASON_MAX_LENGTH}`}
            />
            {hint !== undefined && <Alert severity="info" sx={{ mt: 1 }}>{hint}</Alert>}
        </DialogContent>
        <DialogActions>
            <DeleteButton loading={saving} onClick={handleConfirmDelClick} />
            <CancelButton loading={saving} onClick={handleCancelDelClick} />
        </DialogActions>
    </Dialog>
}
